import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import ItemCalloutMessages from '../item-variant/item-callout-msg'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import InformativeLabel from './partials/InformativeLabel'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

export const InformativeLabelGroup = ({
    product,
    priceData,
    showPromotions = false,
    isCorporateSale = false,
    corporateSaleLabelName = '',
    isFreeGift = false,
    showOutOfStock = false,
}) => {
    const {data: basket} = useCurrentBasket()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const intl = useIntl()
    const longTextLabels = []
    if (product?.c_badgeCommerce1) longTextLabels.push(product.c_badgeCommerce1)
    if (product?.c_badgeCommerce2) longTextLabels.push(product.c_badgeCommerce2)

    let onSalePromo
    if (priceData && priceData?.isOnSale) {
        const {unitPrice, unitListPrice} = priceData
        const listPrice = unitListPrice ?? priceData.listPrice

        const unitPriceWithoutWeeefee =
            unitPrice -
            // Cart unit prices already exclude separately displayed WEEE fees.
            (unitListPrice == null && product?.priceAdjustments?.length
                ? product.priceAdjustments
                      .filter((adj) => adj?.promotionId === 'weeeFee')
                      .reduce((sum, adj) => sum + (adj.price || 0), 0)
                : 0)

        onSalePromo = `-${intl.formatNumber((listPrice - unitPriceWithoutWeeefee) / listPrice, {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}`
    }

    const linkedWarranty = product?.c_UUIDWarranty
        ? basket?.productItems?.find(
              (li) => li?.c_isWarranty === true && li?.c_UUIDWarranty === product.c_UUIDWarranty,
          )
        : null

    const hasSalePrice =
        (priceData?.isOnSale || priceData?.isOnSale === 'true') && priceData?.unitPrice !== 0
    const shouldShowSalePromo = !notShoppableCountry && !isFreeGift && hasSalePrice
    const shouldShowOfficina = product?.c_isOfficina === 'true'
    const shouldShowCorporateSale = isCorporateSale && !isFreeGift
    const shouldShowLongTextLabels = longTextLabels.length > 0
    const shouldShowLinkedWarrantyLabel =
        showPromotions && linkedWarranty?.priceAdjustments?.length > 0

    // Free gift badge must always be rendered on its own.
    //if ticket is raised that free gift badge doesnt match the design just uncomment this :) and fix it to show the correct label
    // if (isFreeGift) {
    //     return <PromoLabel />
    // }

    return (
        <>
            {showPromotions && <ItemCalloutMessages product={product} />}

            {shouldShowLinkedWarrantyLabel && <ItemCalloutMessages product={linkedWarranty} />}

            {shouldShowSalePromo && (
                <InformativeLabel
                    label={onSalePromo}
                    data-testid="product-omnibus-badge"
                    variant="priceInfo"
                />
            )}

            {shouldShowOfficina && (
                <InformativeLabel
                    label={intl.formatMessage({
                        defaultMessage: 'Officina',
                        id: 'informative_label.officina',
                    })}
                    variant="pimInfo"
                    data-testid="product-officina-badge"
                />
            )}
            {product?.c_isNew === 'true' && (
                <InformativeLabel
                    label={intl.formatMessage({
                        defaultMessage: 'New',
                        id: 'informative_label.new',
                    })}
                    variant="pimInfo"
                    data-testid="product-omnibus-badge"
                />
            )}
            {shouldShowLongTextLabels &&
                longTextLabels.map((label, i) => {
                    return label && <InformativeLabel key={i} label={label} variant="pimInfo" />
                })}

            {shouldShowCorporateSale && (
                <InformativeLabel
                    label={corporateSaleLabelName}
                    variant="promoInfo"
                    data-testid="product-corporate-sale-badge"
                />
            )}

            {showOutOfStock && (
                <InformativeLabel
                    label={intl.formatMessage({
                        id: 'use_product.message.out_of_stock',
                        defaultMessage: 'Out of stock',
                    })}
                    variant="stockInfo"
                    data-testid="product-corporate-sale-badge"
                />
            )}
        </>
    )
}

InformativeLabelGroup.propTypes = {
    product: PropTypes.object,
    priceData: PropTypes.object,
    isCorporateSale: PropTypes.bool,
    corporateSaleLabelName: PropTypes.string,
    showPromotions: PropTypes.bool,
    isBundle: PropTypes.bool,
    isFreeGift: PropTypes.bool,
    showOutOfStock: PropTypes.bool,
}
